/**
 * Haptics wrapper — 티켓 오픈 / A상 당첨 / 일반 인터랙션 진동.
 *
 * Notion §1.3.2~1.3.3 매핑.
 */
import { isNative, nativeWarn } from "./index";

export type HapticStyle = "light" | "medium" | "heavy";
export type NotificationFeedback = "success" | "warning" | "error";

/** 일반 충격 진동 — 버튼 탭, 카드 플립 */
export async function triggerHaptic(style: HapticStyle = "medium"): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const map = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    } as const;
    await Haptics.impact({ style: map[style] });
  } catch (err) {
    nativeWarn("haptics:impact", err);
  }
}

/** 알림 진동 — 결과 발표 (A상 당첨 / 오류) */
export async function triggerNotification(type: NotificationFeedback = "success"): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import("@capacitor/haptics");
    const map = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error,
    } as const;
    await Haptics.notification({ type: map[type] });
  } catch (err) {
    nativeWarn("haptics:notification", err);
  }
}

/**
 * 드래그 래칫 진동 — 티켓을 드래그로 뜯을 때 진행도에 따라 "지지직" 단계별 진동.
 * native 는 Capacitor impact, web(안드로이드 Chrome 등)은 navigator.vibrate 로 폴백.
 * (데스크탑 브라우저는 vibrate 미지원 → 조용히 무시)
 */
export async function triggerDragTick(style: HapticStyle = "light"): Promise<void> {
  if (isNative()) {
    await triggerHaptic(style);
    return;
  }
  try {
    const ms = style === "heavy" ? 18 : style === "medium" ? 12 : 7;
    navigator.vibrate?.(ms);
  } catch {
    /* vibrate 미지원/차단 — 무시 */
  }
}

/** Selection 진동 — 휠/슬라이더 탭 변경 */
export async function triggerSelection(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics } = await import("@capacitor/haptics");
    await Haptics.selectionChanged();
  } catch (err) {
    nativeWarn("haptics:selection", err);
  }
}

/** A상 당첨 연출 — heavy + success notification 조합 */
export async function triggerCelebration(): Promise<void> {
  if (!isNative()) return;
  await triggerNotification("success");
  await new Promise((r) => setTimeout(r, 100));
  await triggerHaptic("heavy");
  await new Promise((r) => setTimeout(r, 80));
  await triggerHaptic("heavy");
}

/**
 * "내 차례" 알림 진동 — 포그라운드에선 모달/전환만으론 인지가 약하므로 강한 패턴으로 환기.
 * notification(success) + 긴 진동(Android) + 강한 충격 2회. iOS 는 vibrate 가 약하거나 무시될 수
 * 있어 notification/impact 가 대체한다.
 */
export async function triggerTurnReady(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics } = await import("@capacitor/haptics");
    await triggerNotification("success");
    try {
      await Haptics.vibrate({ duration: 500 });
    } catch {
      /* vibrate 미지원 플랫폼 — 아래 impact 가 대체 */
    }
    await new Promise((r) => setTimeout(r, 120));
    await triggerHaptic("heavy");
    await new Promise((r) => setTimeout(r, 100));
    await triggerHaptic("heavy");
  } catch (err) {
    nativeWarn("haptics:turnReady", err);
  }
}
